/**
 * RecordShipmentModal
 *
 * Modal form for recording a new substrate log shipment to a partner.
 * Smart defaults: auto-selects sole partner/batch, pre-fills from last shipment.
 */

import { useState, useEffect, useMemo } from 'react'
import { toLocalDateString } from '../../utils/dateUtils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'

export default function RecordShipmentModal({ open, onOpenChange, onSubmit, partners, purchases, shipments }) {
  const [formData, setFormData] = useState({
    shipment_date: toLocalDateString(new Date()),
    partner_id: '',
    purchase_id: '',
    log_count: '',
    shipment_cost: '',
    notes: ''
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const activePartners = useMemo(() => (partners || []).filter(p => p.is_active), [partners])
  const availablePurchases = useMemo(() => (purchases || []).filter(p => p.logs_remaining > 0), [purchases])
  const selectedPurchase = availablePurchases.find(p => p.id === formData.purchase_id)

  // Most recent shipment for smart defaults
  const lastShipment = useMemo(() => {
    if (!shipments?.length) return null
    return [...shipments].sort((a, b) => b.shipment_date.localeCompare(a.shipment_date))[0]
  }, [shipments])

  // Apply smart defaults when modal opens
  useEffect(() => {
    if (!open) return

    const defaults = {
      shipment_date: toLocalDateString(new Date()),
      partner_id: '',
      purchase_id: '',
      log_count: '',
      shipment_cost: '',
      notes: ''
    }

    // Auto-select sole partner
    if (activePartners.length === 1) {
      defaults.partner_id = activePartners[0].id
    } else if (lastShipment) {
      defaults.partner_id = lastShipment.partner_id
    }

    // Auto-select sole available purchase
    if (availablePurchases.length === 1) {
      defaults.purchase_id = availablePurchases[0].id
    } else if (lastShipment) {
      // Use last shipment's purchase if it still has logs
      const lastPurchaseStillAvailable = availablePurchases.find(p => p.id === lastShipment.purchase_id)
      if (lastPurchaseStillAvailable) {
        defaults.purchase_id = lastShipment.purchase_id
      }
    }

    // Pre-fill quantity and cost from last shipment
    if (lastShipment) {
      defaults.log_count = String(lastShipment.log_count)
      defaults.shipment_cost = String(lastShipment.shipment_cost)
    }

    setFormData(defaults)
    setError(null)
  }, [open, activePartners, availablePurchases, lastShipment])

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const logCount = parseInt(formData.log_count, 10)
      if (selectedPurchase && logCount > selectedPurchase.logs_remaining) {
        throw new Error(`Only ${selectedPurchase.logs_remaining} logs remaining in selected purchase`)
      }

      await onSubmit({
        shipment_date: formData.shipment_date,
        partner_id: formData.partner_id,
        purchase_id: formData.purchase_id,
        log_count: logCount,
        shipment_cost: parseFloat(formData.shipment_cost) || 0,
        notes: formData.notes.trim() || null
      })
      onOpenChange(false)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const logsRemaining = selectedPurchase && formData.log_count
    ? selectedPurchase.logs_remaining - parseInt(formData.log_count || 0, 10)
    : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record Shipment</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="shipment_date">Shipment Date</Label>
              <Input
                id="shipment_date"
                type="date"
                value={formData.shipment_date}
                onChange={(e) => handleChange('shipment_date', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="partner_id">Partner</Label>
              {activePartners.length === 1 ? (
                <Input value={activePartners[0].name} disabled />
              ) : (
                <Select
                  value={formData.partner_id}
                  onValueChange={(v) => handleChange('partner_id', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {activePartners.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="purchase_id">Source Purchase</Label>
            {availablePurchases.length === 1 ? (
              <Input
                value={`${availablePurchases[0].purchase_code} (${availablePurchases[0].logs_remaining} remaining)`}
                disabled
              />
            ) : (
              <Select
                value={formData.purchase_id}
                onValueChange={(v) => handleChange('purchase_id', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select purchase batch" />
                </SelectTrigger>
                <SelectContent>
                  {availablePurchases.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.purchase_code} ({p.logs_remaining} remaining)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="log_count">Number of Logs</Label>
              <Input
                id="log_count"
                type="number"
                min="1"
                max={selectedPurchase?.logs_remaining || undefined}
                value={formData.log_count}
                onChange={(e) => handleChange('log_count', e.target.value)}
                placeholder="e.g. 10"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="shipment_cost">Shipment Cost (£)</Label>
              <Input
                id="shipment_cost"
                type="number"
                step="0.01"
                min="0"
                value={formData.shipment_cost}
                onChange={(e) => handleChange('shipment_cost', e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Summary */}
          {selectedPurchase && formData.log_count && (
            <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Logs remaining after shipment</span>
                <span className={`font-medium ${logsRemaining < 0 ? 'text-destructive' : ''}`}>
                  {logsRemaining}
                </span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="shipment_notes">Notes</Label>
            <Input
              id="shipment_notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Optional notes..."
            />
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !formData.partner_id || !formData.purchase_id || !formData.log_count}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Record Shipment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
